import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { trackEvent } from "@/hooks/useTracking";
import { posthog } from "@/lib/analytics";

type AuthUser = {
  id: string;
  username: string;
  email: string | null;
  fullName: string | null;
  role: string;
  tier: string;
  isComped?: boolean;
  tosAccepted?: boolean;
  tosVersion?: string;
  impersonating?: boolean;
  adminId?: string;
  adminName?: string;
};

export function useAuth() {
  const [, setLocation] = useLocation();

  const { data: user, isLoading } = useQuery<AuthUser | null>({
    queryKey: ["/api/user"],
    queryFn: async () => {
      const res = await fetch("/api/user", { credentials: "include" });
      if (res.status === 401) return null;
      if (!res.ok) throw new Error("Failed to fetch user");
      return res.json();
    },
    staleTime: Infinity,
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const res = await apiRequest("POST", "/api/auth/login", {
        username: data.email,
        password: data.password,
      });
      return res.json();
    },
    onSuccess: () => {
      trackEvent('user_logged_in', { method: 'email' });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    },
    onError: () => {
      trackEvent('login_failed', { method: 'email' });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: { email: string; password: string; fullName?: string }) => {
      const res = await apiRequest("POST", "/api/auth/register", data);
      return res.json();
    },
    onSuccess: () => {
      trackEvent('user_registered', { method: 'email' });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/auth/logout");
    },
    onSuccess: () => {
      trackEvent('user_logged_out');
      posthog.reset();
      queryClient.setQueryData(["/api/user"], null);
      queryClient.clear();
      setLocation("/");
    },
  });

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    login: loginMutation,
    register: registerMutation,
    logout: logoutMutation,
  };
}
